/**
 * Pure helpers behind the camera connectivity panel (ENG-2457): the step
 * ladder, plain-language labels, the site summary, and turning admin's
 * groups into the Troubleshooter's `Finding` cards so the advice looks the
 * same everywhere.
 *
 * Every integration family checks different steps (a VMS login, a cloud
 * listing, an email arriving…), so the ladder is built from whatever ordered
 * `steps[]` admin sends, with a label for each id.
 */
import {
  Finding,
  FindingOwner,
  FindingSeverity,
} from "../../Troubleshooting/troubleshootingInterfaces";
import {
  CONNECTION_STEPS,
  CameraConnectivity,
  CameraConnectivityStatus,
  ConnectionPath,
  ConnectionPathRef,
  ConnectivityGroup,
  ConnectivityResult,
  ConnectivitySummary,
  FAMILY_STEPS,
  IntegrationFamily,
  StepId,
  StepState,
} from "./siteConnectivityTypes";

export const STEP_LABEL: Record<string, string> = {
  // Connection
  tunnel_up: "WireGuard tunnel up",
  address_in_tunnel: "Address inside the tunnel",
  vpn_up: "VPN up",
  address_routed: "Address routed over the VPN",
  // Direct cameras and recorders
  resolve: "Address found",
  port: "Port reachable",
  login: "Camera login",
  // VMS
  vms_reachable: "VMS reachable",
  vms_login: "VMS login",
  camera_in_vms: "Camera found in the VMS",
  // Cloud
  cloud_login: "Cloud login",
  camera_listed: "Camera listed in the cloud",
  // Video (direct, VMS, cloud)
  stream_open: "Stream opens",
  first_frame: "Video received",
  quality: "Picture size and format",
  // Email (SMTP)
  email_received: "Email received",
  image_attached: "Image attached",
  camera_matched: "Matched to a camera",
  image_readable: "Image readable",
  // Events
  account_linked: "Account linked",
  events_arriving: "Events arriving",
  clip_downloads: "Clip downloads",
  frames_readable: "Frames readable",
};

export const STEP_STATE_TEXT: Record<StepState, string> = {
  pass: "passed",
  fail: "failed",
  not_reached: "not reached",
  unknown: "not known",
};

/** A readable name for any step id, including ones added after this build. */
export const stepLabel = (id: string | null | undefined): string => {
  if (!id) return "an unknown step";
  if (STEP_LABEL[id]) return STEP_LABEL[id];
  const words = id.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * A step's label for one camera: the connection step names the camera's
 * tunnel or VPN when admin sent it ("WireGuard tunnel “Laura home RUT” up"),
 * so a site with several tunnels shows which one a camera stopped at.
 */
export const stepLabelFor = (
  id: string | null | undefined,
  connection?: ConnectionPathRef | null
): string => {
  const name = connection?.name?.trim();
  if (name && id === "tunnel_up" && connection?.kind === "wireguard")
    return `WireGuard tunnel “${name}” up`;
  if (name && id === "vpn_up" && connection?.kind === "vpn")
    return `VPN “${name}” up`;
  return stepLabel(id);
};

export const FAMILY_LABEL: Record<IntegrationFamily, string> = {
  direct: "Cameras or recorder",
  vms: "Video management system (VMS)",
  cloud: "Cloud service",
  smtp: "Email (SMTP)",
  events: "Partner events",
};

export const CONNECTION_LABEL: Record<ConnectionPath, string> = {
  default_path: "Default path",
  wireguard: "WireGuard tunnel",
  vpn: "Site-to-site VPN",
};

/** Admin sends `family` with every result; unknown or missing reads as direct. */
export const familyOf = (
  result: Pick<ConnectivityResult, "family"> | null | undefined
): IntegrationFamily =>
  result?.family && result.family in FAMILY_STEPS
    ? (result.family as IntegrationFamily)
    : "direct";

export const connectionOf = (
  result: Pick<ConnectivityResult, "connection"> | null | undefined
): ConnectionPath =>
  result?.connection && result.connection in CONNECTION_STEPS
    ? (result.connection as ConnectionPath)
    : "default_path";

/** "Email (SMTP) · Default path". */
export const familyConnectionLabel = (
  result: Pick<ConnectivityResult, "family" | "connection">
): string =>
  `${FAMILY_LABEL[familyOf(result)]} · ${
    CONNECTION_LABEL[connectionOf(result)]
  }`;

/**
 * The full camera test (dry run) only opens video, so it covers direct, VMS
 * and cloud sites. For email and partner-event sites the check after deploy
 * is the test.
 */
export const dryRunSupported = (family: IntegrationFamily): boolean =>
  family === "direct" || family === "vms" || family === "cloud";

/**
 * The site's step columns, in order: the connection's steps, then the
 * family's, then any other id a camera reports (in the order first seen), so
 * an id added in admin later still shows.
 */
export const stepColumns = (result: ConnectivityResult): StepId[] => {
  const base = [
    ...CONNECTION_STEPS[connectionOf(result)],
    ...FAMILY_STEPS[familyOf(result)],
  ];
  const reported = (result.cameras ?? []).flatMap((c) =>
    (c.steps ?? []).map((s) => s.id)
  );
  const anyReported = new Set(reported);
  // Optional family steps (e.g. no tunnel step when admin couldn't tell) are
  // kept only if some camera reports them or no camera reports anything.
  const columns = base.filter(
    (id) => anyReported.size === 0 || anyReported.has(id) || !isOptional(id)
  );
  reported.forEach((id) => {
    if (!columns.includes(id)) columns.push(id);
  });
  return columns;
};

/** Connection steps only show when admin reports them. */
const isOptional = (id: string): boolean =>
  Object.values(CONNECTION_STEPS).some((ids) => ids.includes(id));

/**
 * One camera's steps laid over the site's columns: in column order, with a
 * column the camera didn't report as "not known". Without columns, the
 * camera's own steps in the order they came.
 */
export const ladderFor = (
  camera: Pick<CameraConnectivity, "steps">,
  columns?: StepId[]
): { id: StepId; state: StepState }[] => {
  const steps = camera.steps ?? [];
  if (!columns) return steps.map((s) => ({ id: s.id, state: s.state }));
  const byId = new Map(steps.map((s) => [s.id, s.state]));
  const ordered = columns.map((id) => ({
    id,
    state: byId.get(id) ?? ("unknown" as StepState),
  }));
  steps.forEach((s) => {
    if (!columns.includes(s.id)) ordered.push({ id: s.id, state: s.state });
  });
  return ordered;
};

/** The first failed step, if any. */
export const failedStep = (
  camera: Pick<CameraConnectivity, "steps">
): StepId | null =>
  (camera.steps ?? []).find((step) => step.state === "fail")?.id ?? null;

/** What "working" means for the family. */
const WORKING_VERB: Record<IntegrationFamily, string> = {
  direct: "streaming",
  vms: "streaming",
  cloud: "streaming",
  smtp: "sending images",
  events: "sending events",
};

const STATUS_TEXT: Record<CameraConnectivityStatus, string> = {
  streaming: "Streaming",
  not_streaming: "Not streaming",
  unknown: "No report yet",
  unproven: "Reachable, video not yet proven",
  waiting: "Waiting for the first email",
};

export const cameraStatusText = (
  status: CameraConnectivityStatus | string,
  family: IntegrationFamily = "direct"
): string => {
  if (family === "smtp" && status === "streaming") return "Sending images";
  if (family === "smtp" && status === "not_streaming") return "Not working";
  if (family === "events" && status === "streaming") return "Sending events";
  if (family === "events" && status === "not_streaming") return "Not working";
  return STATUS_TEXT[status as CameraConnectivityStatus] ?? String(status);
};

/** Kept for callers that only need the plain words. */
export const CAMERA_STATUS_TEXT = STATUS_TEXT;

/** Why an "unproven" camera isn't a failure. */
export const UNPROVEN_EXPLANATION =
  "Reachable. This camera only sends video when it detects motion, so its video is checked on the first motion event.";

/** SMTP sites before any email: what to check while waiting. */
export const SMTP_WAITING_TITLE =
  "Waiting for the first email from your recorder";
export const SMTP_WAITING_STEPS = [
  "Make sure the recorder or camera is set to email an image on motion or alarm.",
  "Check the email address and server (host and port) it sends to match what this site shows.",
  "Trigger a test: walk past a camera or use the recorder's “test email” button.",
  "If it uses a filename to say which camera sent it, check the filename matches the camera's setting here.",
];

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/** "0 of 23 cameras streaming" (or "sending images" for email sites). */
export const summarySentence = (
  summary: ConnectivitySummary,
  family: IntegrationFamily = "direct"
): string => {
  if (summary.total === 0) return "No cameras to check at this site";
  return `${summary.streaming} of ${plural(
    summary.total,
    "camera",
    "cameras"
  )} ${WORKING_VERB[family]}`;
};

/** The clauses after the headline: what isn't working and what isn't known. */
export const summaryDetails = (
  summary: ConnectivitySummary,
  family: IntegrationFamily = "direct"
): string[] => {
  const parts: string[] = [];
  if (summary.not_streaming > 0)
    parts.push(`${summary.not_streaming} not ${WORKING_VERB[family]}`);
  if (summary.unproven > 0)
    parts.push(`${summary.unproven} reachable, video checked on first motion`);
  if ((summary.waiting ?? 0) > 0)
    parts.push(`${summary.waiting} waiting for the first email`);
  if (summary.unknown > 0) parts.push(`${summary.unknown} with no report yet`);
  return parts;
};

export type SummaryTone = "bad" | "warn" | "ok" | "info";

/**
 * `bad` = nothing works and something has failed: the onboarding failure
 * case (Genesis: 0 of 23). `warn` = some cameras aren't working. `info` =
 * nothing failed but not everything is proven yet (waiting, unproven).
 */
export const summaryTone = (summary: ConnectivitySummary): SummaryTone => {
  if (summary.total === 0) return "info";
  if (summary.not_streaming > 0 && summary.streaming === 0) return "bad";
  if (summary.not_streaming > 0) return "warn";
  if (summary.streaming === summary.total) return "ok";
  return "info";
};

/** An SMTP site where no camera has sent an email yet. */
export const isSmtpWaiting = (result: ConnectivityResult): boolean =>
  familyOf(result) === "smtp" &&
  (result.status === "waiting" ||
    ((result.cameras ?? []).length > 0 &&
      (result.cameras ?? []).every(
        (c) => c.status === "waiting" || c.status === "unknown"
      ) &&
      (result.cameras ?? []).some((c) => c.status === "waiting")));

/** Same counts, recomputed for a subset of cameras. */
export const summarise = (
  cameras: Pick<CameraConnectivity, "status">[]
): ConnectivitySummary => ({
  total: cameras.length,
  streaming: cameras.filter((c) => c.status === "streaming").length,
  not_streaming: cameras.filter((c) => c.status === "not_streaming").length,
  unknown: cameras.filter((c) => c.status === "unknown").length,
  unproven: cameras.filter((c) => c.status === "unproven").length,
  waiting: cameras.filter((c) => c.status === "waiting").length,
});

/**
 * The result narrowed to some cameras (the camera page shows one). Groups
 * keep only those cameras and drop out when empty; the summary is recounted.
 */
export const filterResult = (
  result: ConnectivityResult,
  cameraIds?: number[] | null
): ConnectivityResult => {
  if (!cameraIds || cameraIds.length === 0) return result;
  const keep = new Set(cameraIds);
  const cameras = (result.cameras ?? []).filter((c) => keep.has(c.camera_id));
  const groups = (result.groups ?? [])
    .map((group) => {
      const ids = (group.camera_ids ?? []).filter((id) => keep.has(id));
      return { ...group, camera_ids: ids, count: ids.length };
    })
    .filter((group) => group.count > 0);
  return { ...result, cameras, groups, summary: summarise(cameras) };
};

export const OWNER_MAP: Record<string, FindingOwner> = {
  customer: "customer",
  actuate: "actuate",
  vms: "vms",
};

const groupSeverity = (
  group: ConnectivityGroup,
  cameras: CameraConnectivity[]
): FindingSeverity => {
  const statuses = cameras
    .filter((c) => group.camera_ids.includes(c.camera_id))
    .map((c) => c.status);
  if (statuses.length === 0 || statuses.includes("not_streaming")) return "bad";
  if (statuses.every((status) => status === "unproven" || status === "waiting"))
    return "info";
  return "warn";
};

/** Groups about cameras that are fine carry nothing to fix. */
const isProblemGroup = (
  group: ConnectivityGroup,
  cameras: CameraConnectivity[]
): boolean => {
  if (group.finding || group.reason) return true;
  const inGroup = cameras.filter((c) => group.camera_ids.includes(c.camera_id));
  return inGroup.length === 0 || inGroup.some((c) => c.status !== "streaming");
};

/**
 * One Troubleshooter finding per group, largest first. When admin sent no
 * catalog entry (a reason it doesn't know yet) the card still says where the
 * cameras stopped and what the connector said, and owns up to not knowing.
 */
export const groupsToFindings = (
  result: ConnectivityResult,
  siteId: number
): Finding[] => {
  const cameras = result.cameras ?? [];
  const total = result.summary?.total ?? cameras.length;
  const verb = WORKING_VERB[familyOf(result)];
  return [...(result.groups ?? [])]
    .filter((group) => isProblemGroup(group, cameras))
    .sort((a, b) => b.count - a.count)
    .map((group, index): Finding => {
      const severity = groupSeverity(group, cameras);
      const firstDetail =
        cameras.find((c) => group.camera_ids.includes(c.camera_id) && c.detail)
          ?.detail ?? undefined;
      const stopped = stepLabel(group.stopped_at);
      const finding = group.finding;
      const statuses = cameras
        .filter((c) => group.camera_ids.includes(c.camera_id))
        .map((c) => c.status);
      const allUnknown =
        statuses.length > 0 && statuses.every((s) => s === "unknown");
      const waiting = severity === "info" && statuses.includes("waiting");
      const metricLabel = waiting
        ? "waiting for the first email"
        : severity === "info"
        ? "reachable, video checked on first motion"
        : allUnknown
        ? "no report yet"
        : group.stopped_at
        ? `stop at “${stopped}”`
        : `not ${verb}`;
      const fallback = waiting
        ? {
            title: SMTP_WAITING_TITLE,
            body: "No email has arrived from these cameras since the site was set up. That's expected until the recorder sends its first motion email.",
            steps: SMTP_WAITING_STEPS,
          }
        : severity === "info"
        ? {
            title: "Reachable: video is checked on first motion",
            body: UNPROVEN_EXPLANATION,
            steps: [] as string[],
          }
        : allUnknown
        ? {
            title: "No report yet from these cameras",
            body: "The connector hasn’t reported on these cameras yet. It may still be starting.",
            steps: dryRunSupported(familyOf(result))
              ? [
                  "Check again in a few minutes.",
                  "Or run a full camera test to check them now.",
                ]
              : ["Check again in a few minutes."],
          }
        : {
            title: group.stopped_at
              ? `Cameras stop at “${stopped}”`
              : `Cameras aren’t ${verb}`,
            body: "We don’t have advice for this one yet. What the connector reported is below; send it to support if you need help.",
            steps: [] as string[],
          };
      return {
        // Down-tunnel groups are per tunnel, so the tunnel is part of the id.
        id: `stream-${group.stopped_at ?? "none"}-${group.reason ?? "unknown"}${
          group.context ? `-${group.context.kind}-${group.context.id}` : ""
        }-${index}`,
        severity,
        owner: finding
          ? OWNER_MAP[finding.owner] ?? "shared"
          : severity === "info"
          ? "none"
          : "shared",
        kind: "stream" as const,
        title: finding?.title ?? fallback.title,
        since: null,
        body: finding?.body ?? fallback.body,
        steps: finding?.steps ?? fallback.steps,
        affectedSiteIds: [siteId],
        metric: {
          value: total > 0 ? `${group.count} of ${total}` : String(group.count),
          label: metricLabel,
        },
        actions: [],
        siteId,
        probeCameraIds: group.camera_ids,
        detailLine: firstDetail,
      };
    });
};

/** The catalog title for a camera's reason, for the table's "What to do" column. */
export const adviceForCamera = (
  camera: CameraConnectivity,
  groups: ConnectivityGroup[]
): string | null => {
  const group =
    groups.find((g) => g.camera_ids.includes(camera.camera_id) && g.finding) ??
    groups.find((g) => g.reason && g.reason === camera.reason && g.finding);
  if (!group?.finding) return null;
  return group.finding.steps[0] ?? group.finding.title;
};

/** Why the camera stopped, in words. */
export const reasonForCamera = (
  camera: CameraConnectivity,
  groups: ConnectivityGroup[]
): string | null => {
  if (camera.status === "unproven") return UNPROVEN_EXPLANATION;
  if (camera.status === "waiting") return SMTP_WAITING_TITLE;
  if (camera.status === "streaming") return null;
  const group =
    groups.find((g) => g.camera_ids.includes(camera.camera_id) && g.finding) ??
    groups.find((g) => g.reason && g.reason === camera.reason && g.finding);
  if (group?.finding) return group.finding.title;
  if (camera.status === "unknown")
    return "The connector hasn’t reported on this camera yet.";
  return camera.detail || "The connector didn’t say why.";
};

/** "H.264 · 1280×720 · first frame in 1.2 s", or null when nothing is known. */
export const videoSummary = (
  camera: Pick<
    CameraConnectivity,
    "codec" | "width" | "height" | "ms_to_first_frame"
  >
): string | null => {
  const parts: string[] = [];
  if (camera.codec) parts.push(camera.codec);
  if (camera.width && camera.height)
    parts.push(`${camera.width}×${camera.height}`);
  if (camera.ms_to_first_frame != null)
    parts.push(
      `first frame in ${(camera.ms_to_first_frame / 1000).toFixed(1)} s`
    );
  return parts.length ? parts.join(" · ") : null;
};

/**
 * "H.264 · 1920×1080 · 15 fps · first frame in 1.2 s", or null: the facts
 * shown with a camera's clip. Like videoSummary, plus frame rate.
 */
export const videoFacts = (camera: {
  codec?: string | null;
  width?: number | null;
  height?: number | null;
  fps?: number | null;
  ms_to_first_frame?: number | null;
}): string | null => {
  const parts: string[] = [];
  if (camera.codec) parts.push(camera.codec);
  if (camera.width && camera.height)
    parts.push(`${camera.width}×${camera.height}`);
  if (camera.fps != null && camera.fps > 0)
    parts.push(`${Math.round(camera.fps * 10) / 10} fps`);
  if (camera.ms_to_first_frame != null)
    parts.push(
      `first frame in ${(camera.ms_to_first_frame / 1000).toFixed(1)} s`
    );
  return parts.length ? parts.join(" · ") : null;
};

/** Statuses that are a camera's answer, not a placeholder. */
const REPORTED_STATUSES: ReadonlySet<string> = new Set([
  "streaming",
  "not_streaming",
  "unproven",
  "waiting",
]);

/**
 * Has this camera reported back? While a full test runs, admin lists every
 * camera it dispatched straight away, with no answer yet ("pending", or
 * "unknown" with nothing checked). Those aren't results.
 */
export const cameraReported = (
  camera: Pick<CameraConnectivity, "status" | "steps" | "reason" | "detail">
): boolean => {
  if (REPORTED_STATUSES.has(camera.status)) return true;
  if (camera.status !== "unknown") return false;
  return Boolean(
    camera.reason ||
      camera.detail ||
      (camera.steps ?? []).some((s) => s.state === "pass" || s.state === "fail")
  );
};

export interface RunProgress {
  /** Cameras with a result so far. */
  reported: number;
  /** Cameras in the test. */
  total: number;
  streaming: number;
}

/** How far a running test has got, counting only cameras that answered. */
export const runProgress = (
  result: Pick<ConnectivityResult, "summary" | "cameras"> | null
): RunProgress => {
  const cameras = result?.cameras ?? [];
  const reported = cameras.filter(cameraReported);
  return {
    reported: reported.length,
    total: Math.max(result?.summary?.total ?? 0, cameras.length),
    streaming: reported.filter((c) => c.status === "streaming").length,
  };
};

/** "Testing… 3 of 11 reported · 2 streaming so far" */
export const runProgressSentence = (
  progress: RunProgress,
  family: IntegrationFamily = "direct"
): string =>
  `Testing… ${progress.reported} of ${progress.total} reported · ${progress.streaming} ${WORKING_VERB[family]} so far`;

/** What produced the result, for the panel's header. */
export const sourceDescription = (result: ConnectivityResult): string => {
  if (result.source === "connector_status") {
    const family = familyOf(result);
    if (family === "smtp")
      return "From the emails and images we have received from each camera.";
    if (family === "events")
      return "From the events and clips the partner service has sent for each camera.";
    return "From the connector’s own report on each camera since it last started.";
  }
  const settings =
    result.settings_source === "preview"
      ? "unsynced (preview) settings"
      : "the site’s synced settings";
  const mode =
    result.mode === "burst"
      ? "all cameras at once, like the live connector"
      : "a few cameras at a time";
  return `Full camera test: we opened each camera’s video using ${settings}, ${mode}.`;
};

/** The permission that lets a user edit a site also lets them run a test. */
export const canRunCameraTest = (
  permissions: Record<string, unknown> | null | undefined
): boolean => Boolean(permissions?.edit_cameras || permissions?.add_cameras);

/** "3:42 PM" today, or a date and time otherwise. */
export const formatClock = (iso: string, now: Date = new Date()): string => {
  const at = new Date(iso);
  const sameDay = at.toDateString() === now.toDateString();
  return sameDay
    ? at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : at.toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
};

/** Plain text of a finding and its cameras, for "Copy diagnostic report". */
export const findingReport = (
  finding: Finding,
  cameras: CameraConnectivity[]
): string => {
  const names = cameras
    .filter((c) => finding.probeCameraIds?.includes(c.camera_id))
    .map((c) => c.camera_name);
  return [
    finding.title,
    `${finding.metric.value} cameras ${finding.metric.label}`,
    "",
    finding.body,
    ...finding.steps.map((step, i) => `${i + 1}. ${step}`),
    finding.detailLine ? `\nConnector said: ${finding.detailLine}` : "",
    names.length ? `\nCameras: ${names.join(", ")}` : "",
  ]
    .filter((line) => line !== undefined)
    .join("\n")
    .trim();
};
